import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, take, throwError } from 'rxjs';

import { AuthService } from '../services/auth/auth.service';
import { URLs } from '../../../environments/environment';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);

  // El endpoint de refresh usa el refreshToken como Bearer, no el accessToken
  if (req.url === URLs.refreshToken) {
    const refreshToken = authService.getRefreshToken();
    return next(
      refreshToken
        ? req.clone({ setHeaders: { Authorization: `Bearer ${refreshToken}` } })
        : req
    );
  }

  // Verificación pública de constancias (QR, sin login) -- ver VerificarConstanciaComponent.
  // No debe llevar Authorization ni entrar al flujo de refresh: en este navegador puede haber
  // quedado un token (vencido o no) de una sesión de admin, y si el backend lo rechaza con
  // 401/403 el flujo de refresh de abajo nunca le resuelve nada a esta página pública --
  // se queda colgada en "Verificando folio..." para siempre.
  if (req.url.startsWith(URLs.constanciaVerificar)) {
    return next(req);
  }

  // Adjuntar accessToken a la request
  const token = authService.getToken();
  const authReq = token
    ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
    : req;

  // Si hay un refresh en curso, esperar el nuevo token y reintentar
  if (authService.refreshInProgress) {
    return authService.refreshDone$.pipe(
      take(1),
      switchMap(newToken =>
        next(req.clone({ setHeaders: { Authorization: `Bearer ${newToken}` } }))
      )
    );
  }

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401 || error.status === 403) {
        return authService.refreshAccessToken().pipe(
          // Si el refresh en sí falla, la sesión ya no es válida.
          catchError(refreshErr => {
            authService.logout();
            return throwError(() => refreshErr);
          }),
          // Si el refresh tuvo éxito pero la petición original vuelve a fallar,
          // el token es válido: el problema es de negocio/permiso, no de sesión.
          // Se propaga tal cual, sin forzar logout.
          switchMap(newToken =>
            next(req.clone({ setHeaders: { Authorization: `Bearer ${newToken}` } }))
          )
        );
      }
      return throwError(() => error);
    })
  );
};
